import _ from 'lodash'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import 'dayjs/locale/en'
import 'dayjs/locale/fr'
import { useRef, useMemo, forwardRef, Ref, useImperativeHandle, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { FieldArray, Form, Formik, FormikProps } from 'formik'
import * as yup from 'yup'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import FormHelperText from '@mui/material/FormHelperText'
import FormGroup from '@mui/material/FormGroup'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'

import Accordion from '@/components/ui/accordions/Accordion'
import CheckboxField from '@/components/formik/fields/CheckboxField'
import CheckboxInput from '@/components/ui/inputs/CheckboxInput'
import DatePickerField from '@/components/formik/fields/DatePickerField'
import ErrorMessage from '@/components/formik/messages/ErrorMessage'
import GooglePlacesAutocompleteField from '@/components/formik/fields/GooglePlacesAutocompleteField'
import SelectField from '@/components/formik/fields/SelectField'
import TextField from '@/components/formik/fields/TextField'
import TimePickerField from '@/components/formik/fields/TimePickerField'
import { CreateAvailabilityIntro } from '@/utils/onboarding/intro'
import { useIntro } from '@/hooks/useIntro'
import RadioGroupField from '@/components/formik/fields/RadioGroupField'
import { AvailabilityMeetingPlace } from '@/core/models/meeting-link.model'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import { useSearchFormTemplatesQuery } from '@/core/services/form'
import { FormTemplate } from '@/core/models/form.model'
import { StaffData } from './StaffSelectionTable'
import StaffSelectField from './StaffSelectField'

dayjs.extend(duration)

// Meeting places that can have forms
export const CAN_HAVE_FORMS = [AvailabilityMeetingPlace.SECOND_OPINION]

// Meeting places that should hide dates/recurrence
export const HIDE_SCHEDULING = [AvailabilityMeetingPlace.SECOND_OPINION]

export const periodGenerator = (open: string = '09:00', close: string = '12:00') => ({
  open,
  close,
})

export type AvailabilityFormHandle = {
  resetForm: () => void
  submitForm: () => void
}

export type AvailabilityFormValues = {
  address: {
    description: string
    placeId: string
  } | null
  additionalAddress?: string
  avgMeetingDuration: number
  isRecurrence: boolean
  meetingPlace: AvailabilityMeetingPlace
  notes: string
  recurrenceDays: Array<{ day: number; periods: Array<{ open: string; close: string }> }>
  scheduledDays: Array<{ day: Date; periods: Array<{ open: string; close: string }> }>
  specialities: string[]
  formTemplate?: string
  serviceArea?: {
    allowedPostalCodes?: string | string[]
    center?: { description?: string; placeId?: string; lat?: number; lng?: number } | null
    radiusKm?: number
  } | null
  paymentConfig?: {
    requiresPayment: boolean
    basePrice?: number
    paymentReceiver?: string
    priceModifiers?: Array<{
      meetingPlace: AvailabilityMeetingPlace
      modifier: number
    }>
  }
}

export type AvailabilityFormProps = {
  initialValues: AvailabilityFormValues
  onSubmit: (values: AvailabilityFormValues) => void
  readOnly?: boolean
  specialities: string[]
  staffs?: StaffData[]
}

export const AvailabilityForm = forwardRef(function AvailabilityForm(props: AvailabilityFormProps, ref: Ref<AvailabilityFormHandle>) {
  const { onSubmit, initialValues, readOnly = false, specialities, staffs = [] } = props
  const { i18n, t } = useTranslation()
  const isTooSmall = useIsMobile()

  const formikRef = useRef<FormikProps<AvailabilityFormValues>>(null)

  // Fetch active form templates
  const { data: formTemplatesResponse } = useSearchFormTemplatesQuery({
    search: { isActive: true },
    skip: 0,
    limit: 100,
    orderBy: 'createdAt',
    order: 'DESC'
  })

  const formTemplateOptions = useMemo(() => {
    if (!formTemplatesResponse?.items) return []
    return formTemplatesResponse.items.map((template: FormTemplate) => ({
      label: template.title,
      value: template._id
    }))
  }, [formTemplatesResponse])

  // Effect to update formTemplate value when options are loaded
  useEffect(() => {
    if (formTemplatesResponse?.items && initialValues.formTemplate) {
      const template = formTemplatesResponse.items.find(
        (template: FormTemplate) => template._id === initialValues.formTemplate
      )
      if (template) {
        formikRef.current?.setFieldValue('formTemplate', template._id)
      }
    }
  }, [formTemplatesResponse, initialValues.formTemplate])

  const availabilityMeetingPlaceOptions = useMemo(
    () => [
      { label: t('page.meetingLink.availabilitiesTable.cell.meetingPlace.center'), value: 'CENTER' },
      { label: t('page.meetingLink.availabilitiesTable.cell.meetingPlace.atHome'), value: 'AT_HOME' },
      { label: t('page.meetingLink.availabilitiesTable.cell.meetingPlace.visioConference'), value: 'VISIO_CONFERENCE' },
      { label: t('page.meetingLink.availabilitiesTable.cell.meetingPlace.secondOpinion'), value: 'SECOND_OPINION' },
    ],
    [t]
  )

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        address: yup
          .object({
            description: yup.string(),
            placeId: yup.string(),
          })
          .nullable()
          .when('meetingPlace', {
            is: (value: string) => value === AvailabilityMeetingPlace.CENTER,
            then: (schema) => schema.required(t('error.address.required.message')),
          }),
        avgMeetingDuration: yup.number().required(),
        meetingPlace: yup.string().required(),
        notes: yup.string().optional(),
        isRecurrence: yup.boolean().required(),
        recurrenceDays: yup.array().of(
          yup.object().shape({
            periods: yup.array().of(
              yup
                .object({
                  open: yup
                    .string()
                    .required()
                    .matches(/^\d\d:\d\d$/, t('error.time.invalidFormat.message')),
                  close: yup
                    .string()
                    .required()
                    .matches(/^\d\d:\d\d$/, t('error.time.invalidFormat.message'))
                    .test('is-greater', t('error.time.endTimeSoonerThanStartTime.message'), (value, testContext) => {
                      const { open } = testContext.parent
                      return dayjs(value, 'HH:mm').isSameOrAfter(dayjs(open, 'HH:mm'))
                    }),
                })
                .test('no-overlap', t('error.time.periodsOverlap.message'), (value, testContext) => {
                  const currentPeriod = value
                  const otherPeriods = _.differenceWith(testContext.parent, [currentPeriod], _.isEqual)
                  for (const otherPeriod of otherPeriods) {
                    if (
                      !currentPeriod.open?.match(/^\d\d:\d\d$/) ||
                      !currentPeriod.close?.match(/^\d\d:\d\d$/) ||
                      !otherPeriod.open?.match(/^\d\d:\d\d$/) ||
                      !otherPeriod.open?.match(/^\d\d:\d\d$/)
                    ) {
                      return true
                    }
                    const [currentPeriodOpenHour, currentPeriodOpenMinute] = currentPeriod.open.split(':')
                    const [currentPeriodCloseHour, currentPeriodCloseMinute] = currentPeriod.close.split(':')
                    const [otherPeriodOpenHour, otherPeriodOpenMinute] = otherPeriod.open.split(':')
                    const [otherPeriodCloseHour, otherPeriodCloseMinute] = otherPeriod.close.split(':')
                    if (
                      dayjs.duration({ hours: Number(currentPeriodOpenHour), minutes: Number(currentPeriodOpenMinute) }).asMinutes() <
                      dayjs.duration({ hours: Number(otherPeriodCloseHour), minutes: Number(otherPeriodCloseMinute) }).asMinutes() &&
                      dayjs.duration({ hours: Number(currentPeriodCloseHour), minutes: Number(currentPeriodCloseMinute) }).asMinutes() >
                      dayjs.duration({ hours: Number(otherPeriodOpenHour), minutes: Number(otherPeriodOpenMinute) }).asMinutes()
                    ) {
                      formikRef.current?.setFieldTouched(`${testContext.path}.open`, true, false)
                      formikRef.current?.setFieldTouched(`${testContext.path}.close`, true, false)
                      return false
                    }
                  }
                  return true
                })
            ),
          })
        ),
        formTemplate: yup.string().optional(),
        paymentConfig: yup.object().shape({
          requiresPayment: yup.boolean(),
          basePrice: yup.number().when(['requiresPayment'], {
            is: (requiresPayment: boolean) => requiresPayment === true,
            then: () => yup.number().required(t('error.basePrice.required.message')).min(0, t('error.basePrice.min.message')),
          }),
          paymentReceiver: yup.string().when(['requiresPayment'], {
            is: (requiresPayment: boolean) => requiresPayment === true,
            then: () => yup.string().required(t('error.paymentReceiver.required.message')),
          }),
          priceModifiers: yup.array().of(
            yup.object().shape({
              meetingPlace: yup.string().required(),
              modifier: yup.number().required().min(0, t('error.modifier.min.message')),
            })
          ),
        }),
      }),
    [t, formikRef]
  )

  const specialityOptions = useMemo(() => _.map(specialities, (speciality) => ({ label: speciality, value: speciality })), [specialities])

  const resetForm = () => {
    formikRef.current?.resetForm()
  }

  const submitForm = () => {
    formikRef.current?.submitForm()
  }

  useImperativeHandle(ref, () => ({
    resetForm,
    submitForm,
  }))

  useIntro(CreateAvailabilityIntro)

  return (
    <Formik enableReinitialize initialValues={initialValues} innerRef={formikRef} onSubmit={onSubmit} validationSchema={validationSchema}>
      {({ errors, setFieldValue, handleSubmit, touched, values }) => (
        <Form onSubmit={handleSubmit}>
          <Stack spacing={2} divider={<Divider />}>
            <Stack id="adress">
              <FormGroup>
                <Stack>
                  <RadioGroupField fullWidth name="meetingPlace" options={availabilityMeetingPlaceOptions} variant="contained" />
                </Stack>
              </FormGroup>
              {values.meetingPlace === AvailabilityMeetingPlace.CENTER && (
                <Stack spacing={3} mt={3}>
                  <GooglePlacesAutocompleteField label={t('field.address.label')} name="address" />
                  <TextField
                    fullWidth
                    label={t('field.addressExtra.label')}
                    name="additionalAddress"
                    maxRows={5}
                    minRows={2}
                    multiline
                    placeholder={t('field.addressExtra.placeholder')}
                    readOnly={readOnly}
                  />
                </Stack>
              )}
              {values.meetingPlace === AvailabilityMeetingPlace.AT_HOME && (
                <Stack spacing={3} mt={3}>
                  <Typography variant="subtitle2">{t('field.serviceArea.label')}</Typography>
                  <TextField
                    fullWidth
                    label={t('field.serviceArea.postalCodes')}
                    name="serviceArea.allowedPostalCodes"
                    placeholder={t('field.serviceArea.postalCodesPlaceholder')}
                    helperText={t('field.serviceArea.postalCodesHelp')}
                    readOnly={readOnly}
                  />
                  <GooglePlacesAutocompleteField label={t('field.serviceArea.center')} name="serviceArea.center" />
                  <TextField
                    fullWidth
                    label={t('field.serviceArea.radius')}
                    name="serviceArea.radiusKm"
                    type="number"
                    readOnly={readOnly}
                  />
                </Stack>
              )}
            </Stack>
            <Stack>
              <SelectField
                enableCheckmark
                fullWidth
                label={t('field.speciality.label', { context: 'plural' })}
                multiple
                name="specialities"
                options={specialityOptions}
                readOnly={readOnly}
                id="specialities"
              />
            </Stack>
            {CAN_HAVE_FORMS.includes(values.meetingPlace) && (
              <Stack>
                <SelectField
                  fullWidth
                  label={t('field.formTemplate.label')}
                  name="formTemplate"
                  options={formTemplateOptions}
                  readOnly={readOnly}
                  id="formTemplate"
                />
              </Stack>
            )}

            {!HIDE_SCHEDULING.includes(values.meetingPlace) && (
              <Stack spacing={1} id="scheduleD">
                <FieldArray name="scheduledDays">
                  {({ push: pushDay, remove: removeDay }) => (
                    <Accordion title={t('field.date.label')}>
                      <Stack spacing={1} divider={<Divider />}>
                        {_.map(values.scheduledDays, ({ day }, dayIndex) => (
                          <Stack key={day.toISOString()} direction={isTooSmall ? "column" : "row"} alignItems={isTooSmall ? "flex-end" : "flex-start"} justifyContent={isTooSmall ? "space-between" : "center"} spacing={1}>
                            <Box flex={1} width='100%'>
                              <DatePickerField name={`scheduledDays[${dayIndex}].day`} readOnly={readOnly} />
                            </Box>
                            <FieldArray name={`scheduledDays[${dayIndex}].periods`}>
                              {({ push: pushPeriod, remove: removePeriod }) => (
                                <>
                                  <Stack flex={1} spacing={2} width='100%'>
                                    {_.map(values.scheduledDays[dayIndex].periods, (_period, periodIndex) => {
                                      const openError = _.get(errors, `scheduledDays[${dayIndex}].periods[${periodIndex}].open`)
                                      const closeError = _.get(errors, `scheduledDays[${dayIndex}].periods[${periodIndex}].close`)
                                      const openTouched = _.get(touched, `scheduledDays[${dayIndex}].periods[${periodIndex}].open`)
                                      const closeTouched = _.get(touched, `scheduledDays[${dayIndex}].periods[${periodIndex}].close`)
                                      return (
                                        <Stack key={`${day.toISOString()}-period-${periodIndex}`} spacing={1} sx={{ maxWidth: '300px' }}>
                                          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                                            <TimePickerField
                                              disableHelperText
                                              fullWidth
                                              name={`scheduledDays[${dayIndex}].periods[${periodIndex}].open`}
                                              readOnly={readOnly}
                                            />
                                            <TimePickerField
                                              disableHelperText
                                              fullWidth
                                              name={`scheduledDays[${dayIndex}].periods[${periodIndex}].close`}
                                              readOnly={readOnly}
                                            />
                                            {!readOnly && (
                                              <Tooltip title={t('common:button.delete.tooltip')}>
                                                <IconButton onClick={() => removePeriod(periodIndex)} size="small">
                                                  <DeleteRoundedIcon />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                          </Stack>
                                          {((openTouched && !!openError) || (closeTouched && !!closeError)) && (
                                            <FormHelperText error sx={{ position: 'unset' }}>
                                              <ErrorMessage error={openError || closeError} />
                                            </FormHelperText>
                                          )}
                                        </Stack>
                                      )
                                    })}
                                  </Stack>
                                  {!readOnly && (
                                    <Stack direction="row">
                                      <Tooltip title={t('common:button.add.tooltip')}>
                                        <IconButton
                                          onClick={() => {
                                            const lastPeriod = _.last(values.scheduledDays[dayIndex].periods)
                                            if (lastPeriod) {
                                              const [lastPeriodCloseHour, lastPeriodCloseMinute] = lastPeriod.close.split(':')
                                              const lastPeriodDuration = dayjs.duration({
                                                hours: Number(lastPeriodCloseHour),
                                                minutes: Number(lastPeriodCloseMinute),
                                              })
                                              const nextOpen =
                                                lastPeriodDuration.clone().add(1, 'h').asMinutes() < 24 * 60
                                                  ? lastPeriodDuration.clone().add(1, 'h')
                                                  : lastPeriodDuration.clone().subtract(23, 'h')
                                              const nextOpenDuration = dayjs.duration({ hours: nextOpen.hours(), minutes: nextOpen.minutes() })
                                              const nextClose =
                                                nextOpenDuration.clone().add(1, 'h').asMinutes() < 24 * 60
                                                  ? nextOpenDuration.clone().add(1, 'h')
                                                  : nextOpenDuration.clone().subtract(23, 'h')
                                              pushPeriod(periodGenerator(nextOpen.format('HH:mm'), nextClose.format('HH:mm')))
                                            } else {
                                              pushPeriod(periodGenerator())
                                            }
                                          }}
                                          size="small"
                                        >
                                          <AddRoundedIcon />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title={t('common:button.delete.tooltip')}>
                                        <IconButton onClick={() => removeDay(dayIndex)} size="small">
                                          <DeleteRoundedIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>
                                  )}
                                </>
                              )}
                            </FieldArray>
                          </Stack>
                        ))}
                      </Stack>
                      {!readOnly && (
                        <Stack direction="row" justifyContent="center" mt={2}>
                          <Button
                            onClick={() => {
                              pushDay({ day: new Date(), periods: [periodGenerator()] })
                            }}
                            variant="contained"
                          >
                            {t('common:button.add.label')}
                          </Button>
                        </Stack>
                      )}
                    </Accordion>
                  )}
                </FieldArray>
                <Stack direction="row" sx={{ width: 'fit-content' }}>
                  <CheckboxField label={t('field.isRecurrence.label')} name="isRecurrence" readOnly={readOnly} />
                </Stack>
                <Stack spacing={2}>
                  {values.isRecurrence && (
                    <Stack spacing={2} mt={2} divider={<Divider />}>
                      {_.range(7).map((day) => (
                        <FieldArray key={day} name={`recurrenceDays[${day}].periods`}>
                          {({ push, remove }) => (
                            <Stack direction="row" alignItems={values.recurrenceDays[day].periods.length > 0 ? 'flex-start' : 'center'} spacing={1}>
                              <Box sx={{ flex: 1 }}>
                                <CheckboxInput
                                  checked={!!values.recurrenceDays[day].periods.length}
                                  label={_.capitalize(dayjs().locale(i18n.language).day(day).format('ddd'))}
                                  onChange={(event) => {
                                    if (event.target.checked) {
                                      push(periodGenerator())
                                    } else {
                                      setFieldValue(`recurrenceDays[${day}].periods`, [])
                                    }
                                  }}
                                  readOnly={readOnly}
                                />
                              </Box>
                              <Stack flex={4} spacing={1}>
                                {values.recurrenceDays[day].periods.length > 0 ? (
                                  <>
                                    {_.map(values.recurrenceDays[day].periods, (_period, index) => {
                                      const openError = _.get(errors, `recurrenceDays[${day}].periods[${index}].open`)
                                      const closeError = _.get(errors, `recurrenceDays[${day}].periods[${index}].close`)
                                      const openTouched = _.get(touched, `recurrenceDays[${day}].periods[${index}].open`)
                                      const closeTouched = _.get(touched, `recurrenceDays[${day}].periods[${index}].close`)
                                      return (
                                        <Stack key={`${day}-period-${index}`} spacing={1} sx={{ maxWidth: '300px' }}>
                                          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                                            <TimePickerField
                                              disableHelperText
                                              fullWidth
                                              name={`recurrenceDays[${day}].periods[${index}].open`}
                                              readOnly={readOnly}
                                            />
                                            <TimePickerField
                                              disableHelperText
                                              fullWidth
                                              name={`recurrenceDays[${day}].periods[${index}].close`}
                                              readOnly={readOnly}
                                            />
                                            {!readOnly && (
                                              <Tooltip title={t('common:button.delete.tooltip')}>
                                                <IconButton onClick={() => remove(index)}>
                                                  <DeleteRoundedIcon />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                          </Stack>
                                          {((openTouched && !!openError) || (closeTouched && !!closeError)) && (
                                            <FormHelperText error sx={{ position: 'unset' }}>
                                              <ErrorMessage error={openError || closeError} />
                                            </FormHelperText>
                                          )}
                                        </Stack>
                                      )
                                    })}
                                  </>
                                ) : (
                                  <Typography sx={{ lineHeight: '37px' }}>
                                    {t('page.createWaitingRoom.createWaitingRoomForm.infoSection.openingHours.message.noPeriod')}
                                  </Typography>
                                )}
                              </Stack>
                              {!readOnly && (
                                <Tooltip title={t('common:button.add.tooltip')}>
                                  <IconButton
                                    onClick={() => {
                                      const lastPeriod = _.last(values.recurrenceDays[day].periods)
                                      if (lastPeriod) {
                                        const [lastPeriodCloseHour, lastPeriodCloseMinute] = lastPeriod.close.split(':')
                                        const lastPeriodDuration = dayjs.duration({
                                          hours: Number(lastPeriodCloseHour),
                                          minutes: Number(lastPeriodCloseMinute),
                                        })
                                        const nextOpen =
                                          lastPeriodDuration.clone().add(1, 'h').asMinutes() < 24 * 60
                                            ? lastPeriodDuration.clone().add(1, 'h')
                                            : lastPeriodDuration.clone().subtract(23, 'h')
                                        const nextOpenDuration = dayjs.duration({ hours: nextOpen.hours(), minutes: nextOpen.minutes() })
                                        const nextClose =
                                          nextOpenDuration.clone().add(1, 'h').asMinutes() < 24 * 60
                                            ? nextOpenDuration.clone().add(1, 'h')
                                            : nextOpenDuration.clone().subtract(23, 'h')
                                        push(periodGenerator(nextOpen.format('HH:mm'), nextClose.format('HH:mm')))
                                      } else {
                                        push(periodGenerator())
                                      }
                                    }}
                                  >
                                    <AddRoundedIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          )}
                        </FieldArray>
                      ))}
                    </Stack>
                  )}
                  <TextField fullWidth label={t('field.avgMeetingDuration.label')} name="avgMeetingDuration" readOnly={readOnly} />
                  <TextField fullWidth label={t('field.notes.label')} name="notes" maxRows={5} minRows={2} multiline readOnly={readOnly} />
                </Stack>
              </Stack>
            )}

            <Accordion title={t('form.paymentConfig.title')}>
              <Stack spacing={2}>
                <CheckboxField
                  name="paymentConfig.requiresPayment"
                  label={t('form.paymentConfig.requiresPayment')}
                />

                {values.paymentConfig?.requiresPayment && (
                  <>
                    <TextField
                      fullWidth
                      type="number"
                      name="paymentConfig.basePrice"
                      label={t('form.paymentConfig.basePrice')}
                      InputProps={{
                        endAdornment: <Typography>€</Typography>,
                      }}
                    />

                    <StaffSelectField
                      fullWidth
                      name="paymentConfig.paymentReceiver"
                      label={t('form.paymentConfig.paymentReceiver')}
                      disabled={!values.paymentConfig?.requiresPayment || readOnly}
                      staffs={staffs}
                    />

                    <Typography variant="subtitle2" color="text.secondary" mt={2}>
                      {t('form.paymentConfig.priceModifiers')}
                    </Typography>

                    <FieldArray name="paymentConfig.priceModifiers">
                      {({ push, remove }) => (
                        <Stack spacing={2}>
                          {values.paymentConfig?.priceModifiers?.map((_, index) => (
                            <Stack key={index} direction="row" spacing={2} alignItems="center">
                              <SelectField
                                fullWidth
                                name={`paymentConfig.priceModifiers.${index}.meetingPlace`}
                                label={t('form.paymentConfig.meetingPlace')}
                                options={availabilityMeetingPlaceOptions}
                              />
                              <TextField
                                fullWidth
                                type="number"
                                name={`paymentConfig.priceModifiers.${index}.modifier`}
                                label={t('form.paymentConfig.modifier')}
                                InputProps={{
                                  endAdornment: <Typography>x</Typography>,
                                }}
                              />
                              <IconButton onClick={() => remove(index)}>
                                <DeleteRoundedIcon />
                              </IconButton>
                            </Stack>
                          ))}
                          <Button
                            startIcon={<AddRoundedIcon />}
                            onClick={() => push({ meetingPlace: '', modifier: 1 })}
                          >
                            {t('form.paymentConfig.addModifier')}
                          </Button>
                        </Stack>
                      )}
                    </FieldArray>
                  </>
                )}
              </Stack>
            </Accordion>
          </Stack>
        </Form>
      )}
    </Formik>
  )
})

export default AvailabilityForm
